import React, { useEffect, useMemo, useState } from "react";
import { Monitor, Volume2, Smartphone, Users, CalendarDays, Megaphone, Percent, Sparkles, CreditCard, ShieldCheck, Loader2, ShoppingCart, X, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from "@stripe/stripe-js";
import { useStripe, useElements, CardElement, Elements } from "@stripe/react-stripe-js";
import { Translator } from './translator';


const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";

const PLAN_META = {
  BASIC: {
    key: "BASIC",
    name: "BASIC",
    label: "Con pubblicità",
    monthly: 8.99,              // numeric for math
    priceText: "8,99€",         // string for UI
    gradient: "from-indigo-600 to-indigo-400",
    notes: [
      "Accesso a tutti i tour TecheTour pubblicati in quel periodo",
      "Accesso a tutti i nuovi tour del mese (con pubblicità)",
      "Estensioni disponibili: abbonamento fino a 3 amici/familiari al costo di €4,99 ciascuno per il primo mese",
      "Un'esclusiva sorpresa mensile “Fuori Rotta” al costo promozionale di €7,99 anziché €15,99"
    ],
    // used by Carrello for the Fuori Rotta add-on
    fuoriRottaPrice: 7.99,
    fuoriRottaIncluded: false,
  },

  PLUS: {
    key: "PLUS",
    name: "PLUS",
    label: "Senza pubblicità",
    monthly: 19.99,
    priceText: "19,99€",
    gradient: "from-sky-600 to-rose-500",

    // Add when google approves publishing ads: "Accesso a tutti i nuovi tour del mese, senza pubblicità",
    // Also uncomment deep below: <span className="ml-2 text-sm text-neutral-500">({planI18n.label || plan.label})</span>
    notes: [
      "Accesso a tutti i tour TecheTour pubblicati in quel periodo",
      "Accesso a tutti i nuovi tour del mese, senza pubblicità",
      "Estensioni disponibili: abbonamento fino a 4 amici/familiari al costo di €5,99 ciascuno per il primo mese",
      "Un'esclusiva sorpresa mensile “Fuori Rotta” gratuita"
    ],
    // In PLUS backend function, Fuori Rotta is already included
    fuoriRottaPrice: 0,
    fuoriRottaIncluded: true,
  },
};




const ToggleSwitch = ({ isOn, setIsOn, label, preventOff = false }) => {
  const handleClick = (e) => {
    e.preventDefault();
    // If locked: never allow turning OFF. If it's OFF, allow turning ON once.
    if (preventOff) {
      if (!isOn) setIsOn(true);
      return;
    }
    setIsOn(!isOn);
  };

  return (
    <div
      className={`flex items-center gap-2 ${preventOff ? 'cursor-default' : 'cursor-pointer'}`}
      onClick={handleClick}
      role="switch"
      aria-checked={isOn}
      aria-disabled={preventOff}
      tabIndex={preventOff ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
        <div
          className={`w-10 h-6 rounded-full shadow-inner ${
            isOn ? 'bg-teal-500' : 'bg-gray-300'
          }`}
        />
        <div
          className={`absolute top-0 left-0 w-6 h-6 bg-white border-2 rounded-full shadow transform transition-transform duration-200 ${
            isOn ? 'translate-x-full border-teal-500' : 'translate-x-0 border-gray-300'
          }`}
        />
      </div>
      <span className="text-sm font-medium text-black flex items-center gap-1">
        {label}
        {preventOff && (
          <span className="sr-only">(locked on)</span>
        )}
      </span>
    </div>
  );
};



const Carrello = () => {
  const { translateText, lang } = Translator();

  const baseTextByLang = {
    it: {
      text1: "Tour digitale privato",
      text2: "Durata consigliata:",
      text3: "1 adulto",
      text4: "Lingue: Tutte",
      text5: "Cancellazione gratuita",
      text6: "Riepilogo ordine",
      text7: "Totale",
      text8: "Procedi al pagamento",
      text9: "Potrebbero interessarti anche",
      text10: "Il tuo carrello",
      text11: "Completa l'acquista dei tour digitali",
      text12: "Il tuo carrello è vuoto.",
      text13: "Esplora i tour",
      text14: "La modalità AR è disponibile solo sull’app mobile Te.C.H-e",
      text15: "La modalità VR è disponibile solo sull’app mobile Te.C.H-e",
      text16: "Per goderti l’esperienza AR",
      text17: "L’AR è disponibile solo sull’app mobile Te.C.H-e. Scansiona il QR per scaricarla o continua per attivare l’opzione nel carrello.",
      text18: "Continua",
      text19: "Annulla",
      textApplicaCodice: "Applica codice",
      textPacchetto: "Pacchetto",
      textRimuoverePacchetto: "Rimuovere questo pacchetto dal carrello?",
      textRimuovipacchetto: "Rimuovi pacchetto",
      textincludepac: 'Include 3 tour del pacchetto “Radici Italiane”.',
      textRimuovipiano: "Rimuovi piano",
      textAbbonamento: "Abbonamento",
      textmese: "/mese",
      textnor: "normalmente",
      texteuro: "€",
      textAttiva: "Attiva per includere l’add-on Fuori Rotta in questo abbonamento.",
      textfuori: "Fuori Rotta",
      textesclusiva: "Fuori Rotta esclusiva",
      textcon: "Aggiungi una “Fuori Rotta” esperienza esclusiva sorpresa valida 40 giorni.",

      textapplicato: "Codice applicato:",

      textalert1: "Effettua l'accesso prima di pagare.",
      textalert2: "Errore durante l’applicazione del codice.",
      textalert3: "Abbonamento attivato con successo!",
      textalert4: "Errore durante la rimozione del piano.",
      textalert5: "Accesso richiesto per rimuovere il piano.",
      textalert6: "Errore durante la rimozione del pacchetto.",
      textalert7: "Accesso richiesto per rimuovere il pacchetto.",
      textalert8: "Errore durante la rimozione dal carrello.",
      textalert9: "Accesso richiesto per rimuovere dal carrello.",
      textalert10: "Errore nel salvataggio dell'opzione Fuori Rotta.",
      textalert11: "Seleziona prima un piano.",
      textalert12: "Pagamento: Carta non trovata.",
      textalert13: "Errore durante il pagamento.",
      textalert14: "Pagamento riuscito!",
      textalert15: "Errore rete durante il pagamento.",
      textalert16: "Impossibile applicare il codice.",

      textVerificacodice: "Verifica codice…",
      textConfermaRimuoviPiano: "Sei sicuro di voler rimuovere questo piano dal carrello?",
      textConfermaRimuoviTour: "Sei sicuro di voler rimuovere questo tour dal carrello?",
      textRimuoviDalCarrello: "Rimuovi dal carrello",

      textValido: "Valido",
      textRimasti: "rimasti",
      textCodiceScaduto: "Codice scaduto.",
      textCodiceEsaurito: "Codice già esaurito.",
      textCodiceNonValidoPiano: "Codice non valido per questo piano.",
      textCodiceNonValido: "Codice non valido.",

      textErroreSalvaPiano: "Impossibile salvare il piano.",
      textErroreAttivazioneAbbonamento: "Errore durante l’attivazione dell’abbonamento.",
      textImpossibileCrearePagamento: "Impossibile creare il pagamento.",
      textCodiceApplicato: "Codice applicato!",

      textQty: "Qtà:",
      textExpAR: "Esperienza AR",
      textExpVR: "Esperienza VR",
      textEsploraTour: "Esplora il tour",
      textPlaceholderCodice: "XXXX-XXXX",
      textNomeNonDisp: "Nome non disponibile",

      textDaySing: "giorno",
      textDayPlur: "giorni",
    },

    en: {
      text1: "Private digital tour",
      text2: "Recommended duration:",
      text3: "1 adult",
      text4: "Languages: All",
      text5: "Free cancellation",
      text6: "Order summary",
      text7: "Total",
      text8: "Proceed to payment",
      text9: "You may also like",
      text10: "Your cart",
      text11: "Complete the purchase of digital tours",
      text12: "Your cart is empty.",
      text13: "Browse tours",
      text14: "AR mode is available only in the Te.C.H-e mobile app",
      text15: "VR mode is available only in the Te.C.H-e mobile app",
      text16: "To enjoy the AR experience",
      text17: "AR is available only in the Te.C.H-e mobile app. Scan the QR code to download it or continue to enable the option in the cart.",
      text18: "Continue",
      text19: "Cancel",
      textApplicaCodice: "Apply code",
      textPacchetto: "Package",
      textRimuoverePacchetto: "Remove this package from the cart?",
      textRimuovipacchetto: "Remove package",
      textincludepac: 'Includes 3 tours from the “Radici Italiane” package.',
      textRimuovipiano: "Remove plan",
      textAbbonamento: "Subscription",
      textmese: "/month",
      textnor: "normally",
      texteuro: "€",
      textAttiva: "Activate to include the Fuori Rotta add-on in this subscription.",
      textfuori: "Fuori Rotta",
      textesclusiva: "Exclusive Fuori Rotta",
      textcon: 'Add an exclusive surprise “Fuori Rotta” experience valid for 40 days.',

      textapplicato: "Code applied:",

      textalert1: "Log in before paying.",
      textalert2: "Error while applying the code.",
      textalert3: "Subscription activated successfully!",
      textalert4: "Error while removing the plan.",
      textalert5: "Login required to remove the plan.",
      textalert6: "Error while removing the package.",
      textalert7: "Login required to remove the package.",
      textalert8: "Error while removing from the cart.",
      textalert9: "Login required to remove from the cart.",
      textalert10: "Error while saving the Fuori Rotta option.",
      textalert11: "Select a plan first.",
      textalert12: "Payment: Card not found.",
      textalert13: "Error during payment.",
      textalert14: "Payment successful!",
      textalert15: "Network error during payment.",
      textalert16: "Unable to apply the code.",

      textVerificacodice: "Checking code…",
      textConfermaRimuoviPiano: "Are you sure you want to remove this plan from the cart?",
      textConfermaRimuoviTour: "Are you sure you want to remove this tour from the cart?",
      textRimuoviDalCarrello: "Remove from cart",

      textValido: "Valid",
      textRimasti: "remaining",
      textCodiceScaduto: "Code expired.",
      textCodiceEsaurito: "Code already used up.",
      textCodiceNonValidoPiano: "Code not valid for this plan.",
      textCodiceNonValido: "Invalid code.",

      textErroreSalvaPiano: "Unable to save the plan.",
      textErroreAttivazioneAbbonamento: "Error while activating the subscription.",
      textImpossibileCrearePagamento: "Unable to create payment.",
      textCodiceApplicato: "Code applied!",

      textQty: "Qty:",
      textExpAR: "AR experience",
      textExpVR: "VR experience",
      textEsploraTour: "Explore the tour",
      textPlaceholderCodice: "XXXX-XXXX",
      textNomeNonDisp: "Name not available",

      textDaySing: "day",
      textDayPlur: "days",
    },

    fr: {
      text1: "Tour numérique privé",
      text2: "Durée recommandée :",
      text3: "1 adulte",
      text4: "Langues : toutes",
      text5: "Annulation gratuite",
      text6: "Récapitulatif de la commande",
      text7: "Total",
      text8: "Procéder au paiement",
      text9: "Vous pourriez aussi aimer",
      text10: "Votre panier",
      text11: "Complétez l'achat des tours numériques",
      text12: "Votre panier est vide.",
      text13: "Découvrir les tours",
      text14: "Le mode AR est disponible uniquement sur l’app mobile Te.C.H-e",
      text15: "Le mode VR est disponible uniquement sur l’app mobile Te.C.H-e",
      text16: "Pour profiter de l’expérience AR",
      text17: "L’AR est disponible uniquement sur l’app mobile Te.C.H-e. Scannez le QR pour la télécharger ou continuez pour activer l’option dans le panier.",
      text18: "Continuer",
      text19: "Annuler",
      textApplicaCodice: "Appliquer le code",
      textPacchetto: "Forfait",
      textRimuoverePacchetto: "Retirer ce forfait du panier ?",
      textRimuovipacchetto: "Retirer le forfait",
      textincludepac: 'Inclut 3 tours du forfait « Radici Italiane ».',
      textRimuovipiano: "Retirer l’abonnement",
      textAbbonamento: "Abonnement",
      textmese: "/mois",
      textnor: "normalement",
      texteuro: "€",
      textAttiva: "Activez pour inclure l’add-on Fuori Rotta dans cet abonnement.",
      textfuori: "Fuori Rotta",
      textesclusiva: "Fuori Rotta exclusive",
      textcon: 'Ajoutez une expérience surprise « Fuori Rotta » exclusive valable 40 jours.',

      textapplicato: "Code appliqué :",

      textalert1: "Connectez-vous avant de payer.",
      textalert2: "Erreur lors de l’application du code.",
      textalert3: "Abonnement activé avec succès !",
      textalert4: "Erreur lors de la suppression de l’abonnement.",
      textalert5: "Connexion requise pour supprimer l’abonnement.",
      textalert6: "Erreur lors de la suppression du forfait.",
      textalert7: "Connexion requise pour supprimer le forfait.",
      textalert8: "Erreur lors de la suppression du panier.",
      textalert9: "Connexion requise pour supprimer du panier.",
      textalert10: "Erreur lors de l’enregistrement de l’option Fuori Rotta.",
      textalert11: "Sélectionnez d’abord un abonnement.",
      textalert12: "Paiement : carte introuvable.",
      textalert13: "Erreur lors du paiement.",
      textalert14: "Paiement réussi !",
      textalert15: "Erreur réseau lors du paiement.",
      textalert16: "Impossible d’appliquer le code.",

      textVerificacodice: "Vérification du code…",
      textConfermaRimuoviPiano: "Voulez-vous vraiment supprimer cet abonnement du panier ?",
      textConfermaRimuoviTour: "Voulez-vous vraiment supprimer ce tour du panier ?",
      textRimuoviDalCarrello: "Retirer du panier",

      textValido: "Valide",
      textRimasti: "restants",
      textCodiceScaduto: "Code expiré.",
      textCodiceEsaurito: "Code déjà épuisé.",
      textCodiceNonValidoPiano: "Code non valide pour cet abonnement.",
      textCodiceNonValido: "Code non valide.",

      textErroreSalvaPiano: "Impossible d’enregistrer l’abonnement.",
      textErroreAttivazioneAbbonamento: "Erreur lors de l’activation de l’abonnement.",
      textImpossibileCrearePagamento: "Impossible de créer le paiement.",
      textCodiceApplicato: "Code appliqué !",

      textQty: "Qté :",
      textExpAR: "Expérience AR",
      textExpVR: "Expérience VR",
      textEsploraTour: "Découvrir le tour",
      textPlaceholderCodice: "XXXX-XXXX",
      textNomeNonDisp: "Nom non disponible",

      textDaySing: "jour",
      textDayPlur: "jours",
    },
  };

  const fallbackBase = baseTextByLang.it;

  const [translatedText, setTranslatedText] = useState(
    baseTextByLang[lang] || fallbackBase
  );

  // AUTO UPDATE WHEN LANGUAGE CHANGES
  useEffect(() => {
    // Direct static languages → no API call
    if (lang === "it" || lang === "en" || lang === "fr") {
      setTranslatedText(baseTextByLang[lang]);
      return;
    }

    // Other languages → translate from Italian
    let cancelled = false;

    async function translateAll() {
      try {
        const entries = Object.entries(fallbackBase);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            if (!value) return [key, value];
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setTranslatedText(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setTranslatedText(fallbackBase);
        }
      }
    }

    translateAll();
    return () => {
      cancelled = true;
    };
  }, [lang, translateText]);



  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));
  const [translatedCards, setTranslatedCards] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const stripePromise = loadStripe("pk_live_x6KA4EqDe3erfBuncVb5mVdy00z1vrAmRY");
  const [arStates, setArStates] = useState({});
  const [vrStates, setVrStates] = useState({});
  const [infoOpen, setInfoOpen] = useState({});
  const [qtys, setQtys] = useState({});
  const [arPromptFor, setArPromptFor] = useState(null); // stores the tourID being prompted
  const [arPromptLoading, setArPromptLoading] = useState(false);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPlan, setServerPlan] = useState(null);
  const authUser = useMemo(() => {try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } }, []);
  const payCta = `${translatedText.text8} —€${(serverTotal || 0).toFixed(2)}`;
  const getItemPrice = (item) => parseFloat(item.price) || 0;
  const planKeyFromServer = (serverPlan?.key || "").toUpperCase();
  const plan = planKeyFromServer ? PLAN_META[planKeyFromServer] : null;
  const [codeRaw, setCodeRaw] = useState("");
  const [codeState, setCodeState] = useState({checking:false, valid:null, reason:null, usesLeft:null, expiresAt:null, amount:null});
  const [promoApplied, setPromoApplied] = useState(null); // { firstMonth: number, message?: string }
  const normalizeCode = (s="") =>  s.toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,8);
  const [cartPackages, setCartPackages] = useState([]);
  const [packagesI18n, setPackagesI18n] = useState([]);
  const [planI18n, setPlanI18n] = useState({ label: "", notes: [] });
  const REF_PLAN_KEY   = "ref_plan_key";
  const REF_CODE_KEY   = "ref_code_raw";
  const REF_DISC_KEY   = "ref_discount_amount";

  // Pretty-print raw 8-char code
  const prettyReferralCode = () => {
    const raw = sessionStorage.getItem(REF_CODE_KEY) || "";
    return raw.length === 8 ? `${raw.slice(0,4)}-${raw.slice(4)}` : raw;
  };

  // Compute the plan amount that should be shown in the UI.
  // Priority order:
  // 1) serverPlan.amount (already calculated by backend when a code is applied there)
  // 2) client-side referral from sessionStorage (discount only if it matches current plan)
  // 3) fallback to the regular planPrice
  const resolvePlanAmount = (planKeyFromServer, planPrice, serverPlan) => {
    const serverAmount = Number(serverPlan?.amount ?? NaN);
    if (Number.isFinite(serverAmount)) return serverAmount;

    const refPlan = sessionStorage.getItem(REF_PLAN_KEY);
    const refDisc = parseFloat(sessionStorage.getItem(REF_DISC_KEY) || "0");
    if (refPlan && refPlan === planKeyFromServer && Number.isFinite(refDisc) && refDisc > 0) {
      return Math.max(0, Number(planPrice) - refDisc);
    }
    return Number(planPrice);
  };




  // Show "GRATUITO" when price = 0
  const [freeLabel, setFreeLabel] = useState("GRATUITO");
  useEffect(() => {
    (async () => setFreeLabel(await translateText("GRATUITO")))();
  }, [lang, translateText]);

  const formatPriceLabel = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return "";
    if (n === 0) return freeLabel;
    try {
      return new Intl.NumberFormat(lang === "it" ? "it-IT" : undefined, {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${n.toFixed(2)}€`;
    }
  };




  const onCodeChange = async (val) => {
  // format XXXX-XXXX for UX but send raw to API
  const raw = normalizeCode(val);
  setPromoCode(raw.length > 4 ? `${raw.slice(0,4)}-${raw.slice(4)}` : raw);
  setCodeRaw(raw);

  if (raw.length !== 8 || !planKey) {
    setCodeState({checking:false, valid:null, reason:null, usesLeft:null, expiresAt:null, amount:null});
    return;
  }

  // live check
  setCodeState(s => ({...s, checking:true}));
  try {
    const v = await axios.post(`${BASE}/coupon_validate`, { planKey, code: raw });
    if (v.data?.status === "success") {
      setCodeState({
        checking:false,
        valid: !!v.data.valid,
        reason: v.data.reason || null,
        usesLeft: v.data.usesLeft ?? null,
        expiresAt: v.data.expiresAt ?? null,
        amount: v.data.amount ?? null,
      });
    } else {
      setCodeState({checking:false, valid:false, reason:"server_error"});
    }
  } catch {
    setCodeState({checking:false, valid:false, reason:"network"});
  }
};


const planPrice = Number(
  (serverPlan?.monthlyPrice ?? serverPlan?.price ?? PLAN_META[planKeyFromServer]?.monthly ??0)
);
  const planCurrency = (serverPlan?.currency || "EUR").toUpperCase();

  const fuoriPrice = (serverPlan?.fuoriRottaPrice ?? plan?.fuoriRottaPrice ?? 0);
  const fuoriIncluded = (serverPlan?.fuoriRottaIncluded ?? plan?.fuoriRottaIncluded ?? false);

  const planLabel = planKeyFromServer || "";
  const planKey = planKeyFromServer;
  
  const planAmount = Number((serverPlan?.amount ?? 0).toFixed(2));
  const [fuoriOn, setFuoriOn] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  
  
  
  useEffect(() => {
    if (!authUser?.email) {
      navigate("/login", {
        replace: true,
        state: { redirect: location.pathname + location.search }
      });
    }
  }, [authUser, navigate, location]);




  
  const subTotals = useMemo(() => {
    const base = planPrice;
    return { base, total: +base.toFixed(2) };
  }, [planPrice]);


  const goToPayForSubscription = () => {
    if (!authUser?.email) {
      sessionStorage.setItem("selected_plan", planKey);
      navigate("/login", {
        state: { redirect: `/carrello?plan=${encodeURIComponent(planKey)}` }
      });
      return;
    }
    const amount = subTotals.total.toFixed(2); // equals plan.monthly
    const subscription = {
      type: "subscription",
      planKey,
      amount,
    };
    sessionStorage.setItem("checkout_subscription", JSON.stringify(subscription));
    navigate("/pay", { state: { totalAmount: amount, subscription } });
  };

  
  const savePlanAndGoToPay = async () => {
    if (!authUser?.email) {
      sessionStorage.setItem("selected_plan", planKey);
      navigate("/login", { state: { redirect: `/carrello?plan=${encodeURIComponent(planKey)}` } });
      return;
    }
    try {
      // Save the plan selection server-side
      await axios.post(`${BASE}/checkout/selection`, {
        type: "SUBSCRIPTION",
        planKey,
        amount: subTotals.total,   // numeric
      }, { withCredentials: true });

      // Go to /pay — that page should GET the saved selection
      navigate("/pay");
    } catch (e) {
      alert(e?.response?.data?.error || translatedText.textErroreSalvaPiano);
    }
  };



  const uiTotal = useMemo(() => {
    // If backend already computed a total, prefer it.
    if (Number.isFinite(serverTotal) && serverTotal > 0) return Number(serverTotal.toFixed(2));

    // Fallback: client-side total (items + resolved plan + optional fuori rotta)
    const itemsSum = cart.reduce((sum, item) => sum + getItemPrice(item) * (qtys[item.id] || 1), 0);
    const resolvedPlan = planKeyFromServer ? resolvePlanAmount(planKeyFromServer, planPrice, serverPlan) : 0;
    const fuoriAdd = (!fuoriIncluded && fuoriOn) ? (serverPlan?.fuoriRottaPrice ?? 0) : 0;

    return Number((itemsSum + resolvedPlan + fuoriAdd).toFixed(2));
  }, [cart, qtys, planPrice, fuoriIncluded, fuoriOn, serverPlan, serverTotal, planKeyFromServer]);



  const refreshServerTotal = async () => {
    try {
      const res = await axios.get(`${BASE}/getCart?email=${encodeURIComponent(user.email)}`);
      setCart(res.data.cart || []);
      setCartPackages(res.data.cartPackages || []);
      setServerTotal(Number(res.data.totalAmount || 0));
      setServerPlan(res.data.plan || null);
    } catch (e) {}
  };

  async function activateSubscription() {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user?.email) {
      sessionStorage.setItem("selected_plan", planKey);
      navigate(`/register?plan=${encodeURIComponent(planKey)}`);
      return;
    }
    try {
      await axios.post(`${BASE}/subscribe`, {
        plan: planKey,
      }, { withCredentials: true });
      alert(translatedText.textalert3);
      navigate("/account");
    } catch (e) {
      alert(e?.response?.data?.error || translatedText.textErroreAttivazioneAbbonamento);
    }
  }
/*
  useEffect(() => {
    if (!authUser?.email) return;
    if (!planKey) return;

    // Save plan in cart on the backend
    axios.post(`${BASE}/saveToCart`, {
      email: authUser.email,
      planKey,              // <— new, handled by backend PLAN branch
    }).catch(() => {});
    // fetchCart();
  }, [planKey, authUser?.email]);
*/




  async function applyCode() {
    if (!authUser?.email || !planKey || !codeRaw) return;
    const res = await axios.post(`${BASE}/coupon_apply`, {
      email: authUser.email,
      planKey,
      code: codeRaw
    });
    if (res.data.status === "success") {
      // refresh to pull promoFirstMonth into UI totals
      const r = await axios.get(`${BASE}/getCart?email=${encodeURIComponent(authUser.email)}`);
      setServerTotal(Number(r.data.totalAmount || 0));
      setServerPlan(r.data.plan || null);
      setCodeRaw("");
    } else {
      alert(res.data.message || translatedText.textCodiceNonValido);
    }
  }








  useEffect(() => {
    const fetchCart = async () => {
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");
      if (!storedUser?.email) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(
          `${BASE}/getCart?email=${encodeURIComponent(storedUser.email)}`
        );
        const rawCart = res.data.cart || [];
        setCartPackages(res.data.cartPackages || []);

        // use UI language from Translator(), normalised to 2 letters
        const langKey = (lang || "it").split("-")[0]; // "it", "en", "fr", ...

        const detailedCart = await Promise.all(
          rawCart.map(async (item) => {
            const tourID = item.id;
            const persistedAR = Boolean(item.ar);
            const persistedVR = Boolean(item.vr);

            try {
              const tourRes = await axios.get(
                `${BASE}/getTecheTourByIDNew2?nodeID=${tourID}&lang=${langKey}`
              );
              const data = tourRes.data?.tour || {};

              // 1) NAME + SUBHEADING
              const directName   = data[`Name_${langKey}`];        // Name_en / Name_fr
              const directSub    = data[`Subheading_${langKey}`];  // Subheading_en / Subheading_fr

              // fallback chain: translated name → italian → generic "Name"
              let name = directName || data.Name_it || data.Name || item.name || translatedText.textNomeNonDisp;
              let subheading = directSub || data.Subheading_it || data.Subheading || item.subheading || "";

              // only auto-translate when we DON’T have directName/directSub and langKey != "it"
              if (!directName && langKey !== "it") {
                name = await translateText(name);
              }
              if (!directSub && langKey !== "it" && subheading) {
                subheading = await translateText(subheading);
              }

              // 2) REGION + CATEGORY
              let region = data.Region || "";
              let category = data.PrimaryCategory || "Arte e Creatività";

              if (langKey !== "it") {
                if (region)   region   = await translateText(region);
                if (category) category = await translateText(category);
              }

              return {
                id: tourID,
                name,
                subheading,
                region,
                category,
                price: data.FullPrice || item.price,
                baseprice: data.BasePrice,
                image: data.Image || item.image || "",
                duration: data.duration || "1",
                hasAR: [1, 2, 3].some((i) => data[`tappaAR${i}`] === "YES"),
                hasVR: [1, 2, 3].some((i) => data[`tappaVR${i}`] === "YES"),
                persistedAR,
                persistedVR,
                quantity: item.quantity || 1,
              };
            } catch (err) {
              console.error(`Failed to fetch details for tour ${tourID}`, err);
              return null;
            }
          })
        );

        // toggles
        const arInit = {};
        const vrInit = {};
        const initialQtys = {};

        detailedCart.forEach((item) => {
          if (!item) return;
          arInit[item.id] = item.hasAR ? true : false;
          vrInit[item.id] = item.hasVR ? true : false;
          initialQtys[item.id] = item.quantity || 1;
        });

        setArStates(arInit);
        setVrStates(vrInit);
        setQtys(initialQtys);

        setCart(detailedCart.filter(Boolean));
        setServerTotal(Number(res.data.totalAmount || 0));
        setServerPlan(res.data.plan || null);
      } catch (err) {
        console.error("Failed to fetch cart:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [lang, translateText, translatedText.textNomeNonDisp]);



  useEffect(() => {
    setFuoriOn(Boolean(serverPlan?.fuoriRottaSelected));
  }, [serverPlan]);



  // Translate package titles and "include" sentences
  useEffect(() => {
    (async () => {
      const translated = await Promise.all((cartPackages || []).map(async (p) => {
        const title_i18n = await translateText(p.title || "");
        // If you know how many tours are in the package, prefer p.count; fallback to 3 to match your UI.
        const count = p.count ?? 3;
        // Build the "include" sentence in Italian (your base language) and let Translator handle the target lang.
        const include_src = `Include ${count} tour del pacchetto “${p.title}”.`;
        const include_i18n = await translateText(include_src);
        return { ...p, title_i18n, include_i18n };
      }));
      setPackagesI18n(translated);
    })();
  }, [cartPackages, lang]);



  // Translate plan label and notes
  useEffect(() => {
    (async () => {
      if (!plan) { setPlanI18n({ label: "", notes: [] }); return; }
      const label = await translateText(plan.label);
      const notes = await Promise.all((plan.notes || []).map(n => translateText(n)));
      setPlanI18n({ label, notes });
    })();
  }, [planKeyFromServer, lang]);




  useEffect(() => {
    const fetchRandomTours = async () => {
      try {
        const response = await fetch(
          "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/getAllToursByName"
        );
        const text = await response.text();
        const raw = JSON.parse(JSON.parse(text));

        if (!Array.isArray(raw)) return;

        const shuffled = raw.sort(() => 0.5 - Math.random()).slice(0, 3);

        // normalize language like you do elsewhere
        const langKey = (lang || "it").split("-")[0]; // "it", "en", "fr", ...

        const cards = await Promise.all(
          shuffled.map(async (tour) => {
            // base (Italian) values coming from API
            const baseTitle = tour.name || translatedText.textNomeNonDisp;
            const baseSub = tour.subheading || "";

            let title = baseTitle;
            let subheading = baseSub;

            // for non-Italian UI languages, translate text
            if (langKey !== "it") {
              try {
                title = await translateText(baseTitle);
              } catch {
                title = baseTitle;
              }

              if (baseSub) {
                try {
                  subheading = await translateText(baseSub);
                } catch {
                  subheading = baseSub;
                }
              }
            }

            return {
              title,
              subheading,
              price: tour.price,
              img: `https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/${tour.Image}`,
              tourID: tour.id?.toString(),
              hasAR: tour.hasAR === 1,
              hasVR: tour.hasVR === 1,
            };
          })
        );

        setTranslatedCards(cards);
      } catch (err) {
        console.error("Errore nel recupero o parsing dei tour:", err);
      }
    };

    fetchRandomTours();
  }, [lang, translateText, translatedText.textNomeNonDisp]);










// Auto-consume /carrello?plan=PLUS&code=ABCD-EF12
useEffect(() => {
  (async () => {
    try {
      const params = new URLSearchParams(location.search);
      const qPlan = (params.get("plan") || "").toUpperCase().trim();
      const qCodeRaw = (params.get("code") || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

      if (!qPlan && !qCodeRaw) return;
      if (!authUser?.email) return;

      // 1) Ensure PLAN is on the cart
      if (qPlan) {
        try {
          await axios.post(`${BASE}/saveToCart`, {
            email: authUser.email,
            planKey: qPlan,
          });
        } catch { /* plan may already be there */ }
      }

      // 2) Read the plan key straight from the server (DON’T rely on React state yet)
      let planKeyNow = "";
      try {
        const res = await axios.get(`${BASE}/getCart?email=${encodeURIComponent(authUser.email)}`);
        setServerTotal(Number(res.data.totalAmount || 0));
        setServerPlan(res.data.plan || null);
        planKeyNow = ((res.data?.plan?.key || qPlan) || "").toUpperCase();
      } catch {}

      // 3) Apply code using the planKey we just fetched (not planKeyFromServer state)
      if (qCodeRaw && qCodeRaw.length === 8 && planKeyNow) {
        // reflect in UI
        setPromoCode(qCodeRaw.length > 4 ? `${qCodeRaw.slice(0,4)}-${qCodeRaw.slice(4)}` : qCodeRaw);
        setCodeRaw(qCodeRaw);

        // call API directly with the known plan key
        const r = await axios.post(`${BASE}/coupon_apply`, {
          email: authUser.email,
          planKey: planKeyNow,
          code: qCodeRaw,
        });

        if (r?.data?.status === "success") {
          // optional: show immediate first-month value
          const first = Number(r.data.firstMonth || 0);
          //setPromoApplied({ firstMonth: first, message: translatedText.textCodiceApplicato });
        }

        // refresh to pull discounted amount + discountCode badge
        try {
          const r2 = await axios.get(`${BASE}/getCart?email=${encodeURIComponent(authUser.email)}`);
          setServerTotal(Number(r2.data.totalAmount || 0));
          setServerPlan(r2.data.plan || null);
        } catch {}
      }

      // 4) Clean the URL so it doesn’t re-apply on refresh
      if (location.search.includes("plan=") || location.search.includes("code=")) {
        navigate("/carrello", { replace: true });
      }
    } catch {}
  })();
}, [authUser, location.search, navigate]);








// Referral context from sessionStorage
const getReferralContext = () => {
  const planKey = sessionStorage.getItem(REF_PLAN_KEY);
  const disc = parseFloat(sessionStorage.getItem(REF_DISC_KEY) || "0");
  return { planKey, disc: isNaN(disc) ? 0 : disc };
};
const formatEur = (n) => new Intl.NumberFormat(lang || undefined, { style: "currency", currency: "EUR" }).format(n);





// -------------------------------------------------
// Handlers for toggles and qty changes
// -------------------------------------------------  
const handleQtyChange = async (tourID, newQty) => {
  setQtys(prev => ({ ...prev, [tourID]: newQty }));
  try {
    await axios.post("https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/saveToCart", {
      email: user.email,
      tourID,
      ar: arStates[tourID],
      vr: vrStates[tourID],
      quantity: newQty
    });
    await refreshServerTotal();
  } catch (err) {
    //console.error("Failed to update quantity:", err);
  }
};

//-----------------------------------------------------------------
// AR toggles
//--------------------------------------------------------------
const handleArToggle = async (tourID) => {
  //const willBeOn = !arStates[tourID];
  const willBeOn = true;

  // If turning ON, show custom modal (with QR image). OFF can toggle directly.
/*  if (willBeOn) {
    setArPromptFor(tourID);
    return;
  }*/

  // Turning OFF → proceed immediately
  setArStates(prev => ({ ...prev, [tourID]: willBeOn }));
  try {
  await axios.post(`${BASE}/saveToCart`, {
    email: user.email,
    tourID,
    ar: willBeOn,
    vr: vrStates[tourID],
    quantity: qtys[tourID]
  });
  await refreshServerTotal();
} catch (err) { console.error(err); }
};


const confirmEnableAR = async () => {
  if (!arPromptFor) return;
  const tourID = arPromptFor;
  setArPromptLoading(true);
  const willBeOn = true;

  setArStates(prev => ({ ...prev, [tourID]: willBeOn }));
  try {
    await axios.post("https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/saveToCart", {
      email: user.email,
      tourID,
      ar: willBeOn,
      vr: vrStates[tourID],
      quantity: qtys[tourID]
    });
  } catch (err) {
    console.error("Failed to update AR toggle:", err);
  } finally {
    setArPromptLoading(false);
    setArPromptFor(null);
  }
};

const cancelEnableAR = () => {
  setArPromptFor(null);
};



//-----------------------------------------------------------------
// VR toggles
//--------------------------------------------------------------
const handleVrToggle = async (tourID) => {
  //const newVr = !vrStates[tourID];
  //setVrStates(prev => ({ ...prev, [tourID]: newVr }));
  const newVr = true;
  setVrStates(prev => ({ ...prev, [tourID]: true }));
  
  // persist immediately
  try {
    await axios.post("https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/saveToCart", {
      email: user.email,
      tourID,
      ar: arStates[tourID],
      vr: newVr,
      quantity: qtys[tourID]
    });
    await refreshServerTotal();
  } catch (err) {
    console.error("Failed to update VR toggle:", err);
  }
};




//--------------------------------------------------------------
// Remove item from cart
//--------------------------------------------------------------
  const removeItem = async (id) => {
  if (!user || !user.email) {
    alert(translatedText.textalert9);
    return;
  }

  try {
    await axios.post("https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/removeFromCart", {
      email: user.email,
      tourID: id
    });

    const updatedCart = cart.filter((item) => item.id !== id);
    setCart(updatedCart);

    await refreshServerTotal();
    
    // trigger cart counter refresh
    window.dispatchEvent(new Event("showCartPopup"));

  } catch (err) {
    //console.error("Errore nella rimozione dal carrello:", err);
    alert(translatedText.textalert8);
  }
};



//--------------------------------------------------------------
// Remove package from cart
//--------------------------------------------------------------
const removePackage = async (key) => {
  if (!user?.email) {
    alert(translatedText.textalert7);
    return;
  }
  try {
    await axios.post(`${BASE}/removeFromCart`, {
      email: user.email,
      packageKey: key,
    });
    setCartPackages(prev => prev.filter(p => p.key !== key));
    await refreshServerTotal();
    window.dispatchEvent(new Event("showCartPopup"));
  } catch (e) {
    alert(translatedText.textalert6);
  }
};




const removePlan = async () => {
  if (!user || !user.email || !serverPlan?.key) {
    alert(translatedText.textalert5);
    return;
  }

  try {
    await axios.post(`${BASE}/removeFromCart`, {
      email: user.email,
      planKey: serverPlan.key,
    });

    setServerPlan(null);
    sessionStorage.removeItem("selected_plan");

    // refresh totals from backend to stay in sync
    const res = await axios.get(`${BASE}/getCart?email=${encodeURIComponent(user.email)}`);
    setServerTotal(Number(res.data.totalAmount || 0));

    // IMPORTANT: if the URL still has ?plan=... it would re-render the plan card.
    // Clear the query string so the card disappears:
    if (location.search.includes("plan=") || location.search.includes("subscription=")) {
      navigate("/carrello", { replace: true });
    }
    window.dispatchEvent(new Event("showCartPopup"));
  } catch (err) {
    alert(translatedText.textalert4);
  }
};



//---------------------------------------------------------------
// Fuori Rotta toggle
//---------------------------------------------------------------
const toggleFuoriRotta = async (nextVal) => {
  if (!authUser?.email || !planKey) return;
  try {
    await axios.post(`${BASE}/saveToCart`, {
    email: authUser.email,
    planKey,
    fuoriRotta: nextVal,
  });


    // refresh cart to pick up new totals and flags
    const res = await axios.get(`${BASE}/getCart?email=${encodeURIComponent(authUser.email)}`);
    setServerTotal(Number(res.data.totalAmount || 0));
    setServerPlan(res.data.plan || null);
    setFuoriOn(nextVal);
  } catch (e) {
    alert(translatedText.textalert10);
  }
};






/* Stop adding AR/VR price
  const getItemPrice = (item) => {
    const arOn = arStates[item.id];
    const vrOn = vrStates[item.id];
    const base = parseFloat(item.baseprice);
    // if both: +7, else if one: +5, else 0
    const extra = arStates[item.id] && vrStates[item.id] ? 7 : (arStates[item.id] || vrStates[item.id]) ? 5 : 0;
    return base + extra;
  }; */



/*
  const applyPromoCode = async () => {
  if (!authUser?.email) {
    navigate("/login", { state: { redirect: "/carrello" } });
    return;
  }
  if (!planKey) {
    alert(translatedText.textalert11);
    return;
  }

  try {
    const v = await axios.post(`${BASE}/coupon_validate`, {
      planKey,
      code: codeRaw
    });
    if (v.data?.status === "success" && v.data.valid !== true) {
      const msg = v.data.reason === "expired"
        ? "Codice scaduto."
        : v.data.reason === "no_uses_left"
          ? "Codice già esaurito."
          : "Codice non valido.";
      alert(msg);
      return;
    }

    // Persist promo on the user plan selection (server side)
    const r = await axios.post(`${BASE}/coupon_apply`, {
      email: authUser.email,
      planKey,
      code: codeRaw,
    });

    if (r.data?.status === "success") {
      const first = Number(r.data.firstMonth || 0);
      setPromoApplied({ firstMonth: first, message: translatedText.textCodiceApplicato });

      // save raw (unformatted) code for later confirmation after Stripe payment
      sessionStorage.setItem("applied_discount_code", codeRaw.replace("-", "").trim().toUpperCase());
    } else {
      alert(r.data?.message || translatedText.textalert16);
    }
  } catch (e) {
    alert(translatedText.textalert2);
  }
};
*/



async function applyPromoCode() {
  if (!authUser?.email || !planKey || !codeRaw) return;
  const r = await axios.post(`${BASE}/coupon_apply`, {
    email: authUser.email, planKey, code: codeRaw
  });
  if (r.data?.status === "success") {
    const first = Number(r.data.firstMonth || 0);
    //setPromoApplied({ firstMonth: first, message: translatedText.textCodiceApplicato });
    sessionStorage.setItem("applied_discount_code", codeRaw); // confirm later after payment
    // (optional) refresh cart/plan to reflect promo
    const res = await axios.get(`${BASE}/getCart?email=${encodeURIComponent(authUser.email)}`);
    setServerTotal(Number(res.data.totalAmount || 0));
    setServerPlan(res.data.plan || null);
  } else {
    alert(r.data?.message || translatedText.textCodiceNonValido);
  }
}



  const getTotalPrice = () => {
    return cart
      .reduce((sum, item) => sum + getItemPrice(item) * qtys[item.id], 0)
      .toFixed(2);
  };



  const handleCheckout = async () => {
    setCheckoutLoading(true);
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user || !user.email) {
      alert(translatedText.textalert1);
      setCheckoutLoading(false);
      return;
    }

    try {
      const res = await axios.post("https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/createPaymentIntent", {
        // amount: getTotalPrice(),         // old: calculated with AR/VR prices
        amount: Number.isFinite(serverTotal) && serverTotal > 0
        ? serverTotal.toFixed(2)
        : getTotalPrice(),
        userEmail: user.email,
      });

      const clientSecret = res.data.clientSecret;
      const stripe = await stripePromise;
      const elements = stripe.elements();
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        alert(translatedText.textalert12);
        return;
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: user.email,
          },
        },
      });

      if (result.error) {
        //console.error(result.error.message);
        alert(translatedText.textalert13);
      } else if (result.paymentIntent.status === "succeeded") {
      // 1) mark discount as consumed (only if a code was applied)
      try {
        if (serverPlan?.discountCode) {
          await axios.post(`${BASE}/discount_confirm_use`, {
            email: user.email,
            code: serverPlan.discountCode, // plain 8 chars or with hyphen — your backend normalizes
          });
        }
      } catch (_) {
        // swallow – even if this fails, the payment already succeeded
      }

      // 2) refresh cart/plan totals from backend to reflect the consumed code
      try {
        const r = await axios.get(`${BASE}/getCart?email=${encodeURIComponent(user.email)}`);
        setServerTotal(Number(r.data.totalAmount || 0));
        setServerPlan(r.data.plan || null);
      } catch (_) {}

      setCheckoutSuccess(true);
      alert(translatedText.textalert14);
    }

    } catch (err) {
      //console.error("Errore:", err);
      alert(translatedText.textalert13);
    } finally {
      setCheckoutLoading(false);
    }
  };







const startCheckout = async () => {
  const u = JSON.parse(localStorage.getItem("user") || "null");
  if (!u?.email) { alert(translatedText.textalert1); return; }

  try {
    const res = await fetch(`${BASE}/createPaymentIntent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: u.email,
        successUrl: window.location.origin + "/pay/success",
        cancelUrl:  window.location.origin + "/carrello",
      })
    });
    const data = await res.json();

    // Server says it's a subscription (PLAN): redirect to Stripe Checkout
    if (data?.flow === "subscription" && data?.checkoutUrl) {
      window.location.assign(data.checkoutUrl);
      return;
    }

    // Server says it's a one-off (PACKAGE or TOURS): go to /pay to render PaymentElement
    if (data?.flow === "one_off" && data?.clientSecret) {
      sessionStorage.setItem("teche_pi_client_secret", data.clientSecret);
      sessionStorage.setItem("teche_pi_total", String(data.totalAmount || 0));
      navigate("/pay");
      return;
    }

    alert(data?.message || translatedText.textImpossibileCrearePagamento);
  } catch (e) {
    alert(translatedText.textalert15);
  }
};










  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <Loader className="animate-spin text-blue-500" size={48} />
        <p className="mt-4 text-lg font-semibold text-black">Te.C.H-e</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 mt-12">
      {!plan && (
        <>
          <h1 className="text-4xl font-bold mb-6 text-center">{translatedText.text10}</h1>
          <h2 className="text-2xl mb-6 text-center text-black">{translatedText.text11}</h2>
        </>
      )}

      {(cart.length === 0 && cartPackages.length === 0 && !checkoutSuccess && !plan) && (
        <div className="text-center text-black">
          <ShoppingCart size={48} className="mx-auto mb-4" />
          <p>{translatedText.text12}</p>
          <Link to="/choose" className="text-blue-600 no-underline hover:no-underline">{translatedText.text13}</Link>
        </div>
      )}

<div className="flex flex-col lg:flex-row justify-between items-start gap-6 w-full">




        
      
        {/* Left side: Cart cards */}
        <div className="flex flex-col gap-6 w-full lg:w-4/4">
        <AnimatePresence>



            {/* Subscription summary (if a plan was selected) */}
            {plan && (
            <motion.div
              key={`plan-${planKey}`}
              className="flex bg-white rounded-xl overflow-hidden p-4 mb-4 relative -ml-[7%]"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute top-2 right-2 bg-white rounded-full p-1 z-10">
                <button
                  onClick={() => {
                    if (window.confirm(translatedText.textConfermaRimuoviPiano)) {
                      removePlan();
                    }
                  }}
                  className="bg-white p-1 rounded-full"
                  title={translatedText.textRimuovipiano}
                >
                  <X className="text-black w-8 h-8" />
                </button>
              </div>
              {/* Left: visual block (like the tour image) */}
              <div className="relative w-w-xs max-w-xs h-w-xs flex-shrink-0">
                <div className={`w-full h-full rounded-xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center`}>
                  <div className="text-center text-white px-3">
                    <div className="text-xs opacity-90 mb-1">{planI18n.label || plan.label}</div>
                    <div className="text-2xl font-semibold tracking-wide">{planKeyFromServer}</div>
                    <div className="text-sm mt-1">{translatedText.texteuro} {planPrice.toFixed(2)}{translatedText.textmese}{translatedText.textnor} </div>
                  </div>
                </div>
                <div className="absolute top-2 left-2 bg-white/90 text-[#324B8E] text-xs font-semibold px-2 py-1 rounded-full shadow">
                  {translatedText.textAbbonamento}
                </div>
              </div>

              {/* Right: content */}
              <div className="ml-4 flex flex-col justify-between flex-grow">
                <div>
                  <div className="flex items-center text-lg font-semibold text-black relative">
                    {planKey}
{/*                    <span className="ml-2 text-sm text-neutral-500">({planI18n.label || plan.label})</span>            */}
                    {(() => {
                      const refPlan = sessionStorage.getItem(REF_PLAN_KEY);
                      const refDisc = parseFloat(sessionStorage.getItem(REF_DISC_KEY) || "0");
                      if (!serverPlan?.discountCode && refPlan === planKeyFromServer && Number.isFinite(refDisc) && refDisc > 0) {
                        return (
                          <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-xs px-2 py-[2px] border border-emerald-200">
                            −{formatEur(refDisc)}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>


                  {/* Feature bullets */}
                  <ul className="mt-2 space-y-1 text-sm text-neutral-700">
                    {(planI18n.notes.length ? planI18n.notes : plan.notes).map((n, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="mt-[2px]">•</span>
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Fuori Rotta add-on toggle */}
                  {!fuoriIncluded && fuoriPrice > 0 && (
                    <div className="mt-3">
                      {planKeyFromServer === 'BASIC' && !fuoriIncluded && fuoriPrice > 0 && (
                        <ToggleSwitch
                          isOn={fuoriOn}
                          setIsOn={() => toggleFuoriRotta(!fuoriOn)}
                          label={
                            <span className="inline-flex items-center gap-1">
                              {translatedText.textesclusiva} (+€{fuoriPrice.toFixed(2)})
                              {/* Info List */}
                              <span
                                className="relative inline-flex"
                                onMouseEnter={() => setInfoOpen(prev => ({ ...prev, plan: true }))}
                                onMouseLeave={() => setInfoOpen(prev => ({ ...prev, plan: false }))}
                              >
                                <AlertCircle className="w-5 h-5 text-gray-400 cursor-pointer" />
                                {infoOpen?.plan && (
                                  <div className="absolute z-50 top-6 right-0 mt-2 w-72 bg-white border rounded-lg shadow p-3 text-sm text-neutral-800">
                                    <div className="mb-1">
                                      <em>{translatedText.textcon}</em>
                                    </div>
                                  </div>
                                )}
                              </span>
                            </span>
                          }
                        />
                        )}


                      <div className="text-xs text-neutral-500 mt-1">
                        {translatedText.textAttiva}
                      </div>
                    </div>
                  )}

                </div>




                <hr className="my-2 border-black" />

                {/* Footer: price + CTA (NO item/qty here) */}
                <div className="flex items-center gap-3">
                <input
                  value={codeRaw.length > 4 ? `${codeRaw.slice(0,4)}-${codeRaw.slice(4)}` : codeRaw}
                 onChange={(e) => onCodeChange(e.target.value)}
                  placeholder={translatedText.textPlaceholderCodice}
                  maxLength={9}
                  className="border rounded px-3 py-2 text-sm"
                />
                <button
                  onClick={(e) => { e.stopPropagation(); applyPromoCode(); }}
                  className="px-3 py-2 rounded bg-[#324B8E] text-white text-sm"
                  disabled={!codeRaw || codeRaw.length !== 8 || codeState.valid !== true || codeState.checking}
                >
                  {translatedText.textApplicaCodice}
                </button>

{/* tiny status line */}
<div className="text-xs mt-1">
  {codeState.checking && <span>{translatedText.textVerificacodice}</span>}
  {codeState.valid === true && (
    <span className="text-green-700">
      {translatedText.textValido} 
      {typeof codeState.usesLeft === "number" ? ` (${translatedText.textRimasti}: ${codeState.usesLeft})` : ""}.
    </span>
  )}
  {codeState.valid === false && (
    <span className="text-red-700">
      {codeState.reason === "expired" ? translatedText.textCodiceScaduto :
      codeState.reason === "no_uses_left" ? translatedText.textCodiceEsaurito :
      codeState.reason === "not_found" ? translatedText.textCodiceNonValidoPiano :
      translatedText.textCodiceNonValido}
    </span>
  )}
</div>



<span className="ml-auto text-2xl font-semibold">
{resolvePlanAmount(planKeyFromServer, planPrice, serverPlan).toFixed(2)}€
{/*
{Number(serverPlan?.amount ?? promoApplied?.firstMonth ?? planPrice).toFixed(2)}€

{serverPlan?.discountCode ? <span className="text-sm font-normal"> (1° mese)</span> : "/mese"}
*/}





{promoApplied?.message && (
<span className="ml-auto text-2xl font-semibold">{promoApplied.message}</span>
)}
</span>


{/*  discount price
<span className="ml-auto text-2xl font-semibold">
  {Number(serverPlan?.amount ?? promoApplied?.firstMonth ?? planPrice).toFixed(2)}€
</span>
*/}



              </div>
              {/* Chip showing applied code */}
{serverPlan?.discountCode && (
  <div className="mt-2 text-xs text-green-700">
    {translatedText.textapplicato} {serverPlan.discountCode.length === 8
      ? `${serverPlan.discountCode.slice(0,4)}-${serverPlan.discountCode.slice(4)}`
      : serverPlan.discountCode}
  </div>
)}



{/* Session-only referral (when backend hasn't yet set discountCode) */}
{!serverPlan?.discountCode && (() => {
  const refPlan = sessionStorage.getItem(REF_PLAN_KEY);
  const refDisc = parseFloat(sessionStorage.getItem(REF_DISC_KEY) || "0");
  if (refPlan && refPlan === planKeyFromServer && Number.isFinite(refDisc) && refDisc > 0) {
    return (
      <div className="mt-2 text-xs text-green-700">
        {translatedText.textapplicato} {prettyReferralCode()} • −{formatEur(refDisc)}
      </div>
    );
  }
  return null;
})()}


              </div>
            </motion.div>
          )}















{packagesI18n.map((p) => (
  <motion.div
    key={`pkg-${p.key}`}
    className="flex bg-white rounded-xl overflow-hidden p-4 mb-4 relative -ml-[7%]"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.3 }}
  >
    {/* Left visual */}
    <div className="relative w-w-xs max-w-xs h-w-xs flex-shrink-0">
        <img
          src={`${process.env.PUBLIC_URL}/Pacchetto.jpg`}
          alt={p.title_i18n || translatedText.textPacchetto}
          className="w-w-xs max-w-xs h-w-xs object-cover rounded-xl"
        />
      
      <div className="absolute top-2 left-2 bg-white/90 text-[#324B8E] text-xs font-semibold px-2 py-1 rounded-full shadow">
        {translatedText.textPacchetto}
      </div>
    </div>

    {/* Remove */}
    <div className="absolute top-2 right-2 bg-white rounded-full p-1 z-10">
      <button
        onClick={() => {
          if (window.confirm(translatedText.textRimuoverePacchetto)) {
            removePackage(p.key);
          }
        }}
        className="bg-white p-1 rounded-full"
        title={translatedText.textRimuovipacchetto}
      >
        <X className="text-black w-8 h-8" />
      </button>
    </div>

    {/* Right content */}
    <div className="ml-4 flex flex-col justify-between flex-grow">
      <div>
        <div className="flex items-center text-lg font-semibold text-black relative">
          {p.title_i18n || p.title}
        </div>
        <div className="text-sm text-neutral-600 mt-1">
          {p.include_i18n || translatedText.textincludepac}
        </div>
      </div>

      <hr className="my-2 border-black" />

      <div className="flex items-center justify-between">
        <span className="text-sm text-neutral-500"> {/*    text to left        */}       </span>
        <span className="text-2xl font-semibold">{p.amount.toFixed(2)}€</span>
      </div>
    </div>
  </motion.div>
))}









          {cart.map((item) => (
          <motion.div
            key={item.id}
            className="flex bg-white rounded-xl overflow-hidden p-4 mb-4 relative -ml-[7%]"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            {/* Left: Image */}
            <div className="relative w-w-xs max-w-xs h-w-xs flex-shrink-0">
              <img
                src={`https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/${item.image}`}
                alt={item.name}
                className="w-w-xs max-w-xs h-w-xs object-cover rounded-xl"
              />

            </div>





              <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                <button
                  onClick={() => { 
                    if (window.confirm(translatedText.textConfermaRimuoviTour)) {
                      //console.log('Removing item', item.id);
                      removeItem(item.id)
                    }
                  }}
                  className="bg-white p-1 rounded-full"
                  title={translatedText.textRimuoviDalCarrello}
                >
                  <X className="text-black w-8 h-8" />
                </button>
              </div>






            {/* Title */}
            <div className="ml-4 flex flex-col justify-between flex-grow">
              <div>
                <div className="flex items-center text-lg font-semibold text-black relative">
                  {item.name}
                </div>

                <div className="flex items-center text-lg font-semibold text-black relative">
                  {item.subheading}




            {/* Info List */}
            <div
  className="relative ml-2"
  onMouseEnter={() => setInfoOpen((prev) => ({ ...prev, [item.id]: true }))}
  onMouseLeave={() => setInfoOpen((prev) => ({ ...prev, [item.id]: false }))}
>
  <AlertCircle className="w-5 h-5 text-gray-400 cursor-pointer" />

  {infoOpen[item.id] && (
    <div className="absolute z-50 top-6 right-0 mt-2 w-64 bg-white border rounded text-sm text-center">
      <div className="mb-2">
        <span>{translatedText.text1}</span>
      </div>
      <div className="mb-2">
        <span>{translatedText.text2} {item.duration} {Number.parseInt(item.duration) === 1 ? translatedText.textDaySing : translatedText.textDayPlur}</span>
                                     

      </div>
      <div className="mb-2">
        <span>{translatedText.text3}</span>
      </div>
      <div className="mb-2">
        <span>{translatedText.text4}</span>
      </div>
    </div>
  )}
</div>
</div>



<div className="flex items-center text-base text-black relative mt-2 mb-2">
  {item.region}
</div>

<div className="flex items-center text-base text-[#324B8E] relative">
  {item.category}
</div>






          </div>


            {/* AR/VR */}
            <div className="mt-4 space-y-2 text-sm font-semibold">
            {item.hasAR && (
  <ToggleSwitch
    isOn={arStates[item.id]}
    setIsOn={() => handleArToggle(item.id)}
    label={translatedText.textExpAR}
    preventOff
  />
)}

{item.hasVR && (
  <ToggleSwitch
    isOn={vrStates[item.id]}
    setIsOn={() => handleVrToggle(item.id)}
    label={translatedText.textExpVR}
    preventOff
  />
)}



            </div>



<hr className="my-2 border-black" />

{/* base price */}
<div className="flex justify-between items-center">
  {/* Qty selector */}
  <div className="flex items-center text-sm">
    <label htmlFor={`qty-${item.id}`} className="mr-2">{translatedText.textQty}</label>
    <input
      type="number"
      min="1"
      max="99"
      step="1"
      id={`qty-${item.id}`}
      className="w-16 px-1 py-0.5 border rounded text-sm"
      value={qtys[item.id]}
      onChange={e => {
        const raw = parseInt(e.target.value || '1', 10);
        const val = Math.min(99, Math.max(1, isNaN(raw) ? 1 : raw));
        handleQtyChange(item.id, val);
      }}
    />
  </div>

  {/* Price × qty */}
  <span className="text-2xl font-semibold">
    {(getItemPrice(item) * qtys[item.id]).toFixed(2)}€
  </span>
</div>







</div>





          </motion.div>
          ))}





        </AnimatePresence>
  


  
        </div>



  {/* Right side: Calculation box */}
  {(cart.length > 0 || cartPackages.length > 0 || !!plan) && (
  <div className="bg-white rounded-xl p-6 w-full lg:w-2/4 self-start ml-auto">
    <h2 className="text-xl text-black font-bold mb-4">{translatedText.text6}</h2>

    {planLabel && (
      <>
        <div className="flex justify-between text-sm mb-1">
          <span>{translatedText.textAbbonamento} {planLabel}</span>
        {/*  <span>{Number(serverPlan?.amount ?? promoApplied?.firstMonth ?? planPrice).toFixed(2)}€</span> */}
          <span>{resolvePlanAmount(planKeyFromServer, planPrice, serverPlan).toFixed(2)}€</span>

        </div>

        {/* add-on visibility 
        {!serverPlan?.fuoriRottaIncluded && serverPlan?.fuoriRottaSelected && serverPlan?.fuoriRottaPrice > 0 && (
          <div className="flex justify-between text-xs mb-2 pl-4">
            <span>+ {translatedText.textfuori}</span>
            <span>{serverPlan.fuoriRottaPrice.toFixed(2)}€</span>
          </div>
        )}*/}
      </>
    )}



    {cart.map((item) => (
    <div key={item.id} className="flex justify-between text-sm mb-2">
      <span>{item.name}</span>
      <span>{(getItemPrice(item) * qtys[item.id]).toFixed(2)}€ </span>
    </div>
  ))}

  {packagesI18n.map((p) => (
  <div key={`pkgsum-${p.key}`} className="flex justify-between text-sm mb-2">
    <span>{p.title_i18n || p.title}</span>
    <span>{p.amount.toFixed(2)}€</span>
  </div>
))}

  <hr className="my-2 border-t border-black" />



  <div className="flex justify-between text-2xl font-bold">
    <span>{translatedText.text7}</span>
    {/* Total price with AR/VR extra prices
    <span>{getTotalPrice()}€</span>*/}
    <span>{uiTotal.toFixed(2)}€</span>
  </div>
    <button
      // Omar UnComment this 
     /* onClick={() => {
        if (!planKeyFromServer) return; // no plan chosen
        const qs = new URLSearchParams();
        qs.set("plan", planKeyFromServer);          // PLUS or BASIC
        if (planKeyFromServer === "BASIC" && fuoriOn) qs.set("fuori", "1"); // add-on only for BASIC
        navigate(`/pay?${qs.toString()}`);
      }}
      className="!bg-[#324B8E] !text-white !px-6 !py-2 !mt-4 !rounded-lg disabled:opacity-50 w-full"
      disabled={!planKeyFromServer}
      */onClick={() => {
        if (planKeyFromServer) {
          const qs = new URLSearchParams();
          qs.set("plan", planKeyFromServer); // PLUS or BASIC
          if (planKeyFromServer === "BASIC" && fuoriOn) qs.set("fuori", "1");
          navigate(`/pay?${qs.toString()}`);
          return;
        }

        // ONE-OFF flow (only tours and/or packages)
        if ((cart?.length || 0) > 0 || (cartPackages?.length || 0) > 0) {
          startCheckout();
          return;
        }
      }}
      className="!bg-[#324B8E] !text-white !px-6 !py-2 !mt-4 !rounded-lg disabled:opacity-50 w-full"
      disabled={
        // disable only if there is literally nothing to buy or total is 0
        !planKeyFromServer && (uiTotal <= 0 || ((cart?.length || 0) === 0 && (cartPackages?.length || 0) === 0))
      }
    >
      {translatedText.text8}
    </button>
  </div>
      )}














      </div>








<div className="text-2xl font-bold text-center mt-16 mb-16">
  {translatedText.text9}
  </div>

<div className="cards-container flex flex-wrap justify-center gap-4 mb-32">
  {translatedCards.map((card, index) => {
    const cardContent = (
      <div className="card bg-white rounded-lg shadow-md overflow-hidden w-72 flex flex-col h-[500px]" key={index}>
        <img src={card.img} alt={`Card ${index}`} className="w-full h-[250px] object-cover" />
        <div className="card-content flex flex-col justify-between flex-grow">
          <div className="card-top min-h-[5rem] flex flex-col items-center justify-center text-center overflow-hidden px-2">
            <h3 className="text-black font-semibold text-[1rem] leading-tight">
              {card.title}
            </h3>

            <h3 className="text-black font-semibold text-[1rem] leading-tight">
              {card.subheading}
            </h3>
          </div>

          <div className="card-price-icons flex justify-between items-center mt-2 px-2 pb-2">
            <p className="font-bold">{formatPriceLabel(card.price)}</p>
            <div className="card-icons flex space-x-2">
              {card.hasVR && (
                <img src="https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/Teche/vr-icon.png" alt="VR icon" className="w-6 h-6" />
              )}
              {card.hasAR && (
                <img src="https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/Teche/ar-icon.png?v=2" alt="AR icon" className="w-6 h-6" />
              )}
            </div>
          </div>
        </div>
      </div>
    );
    return card.tourID ? (
      <Link
        to={`/tourPreview/${card.tourID}`}
        key={index}
        className="no-underline"
        title={translatedText.textEsploraTour}
      >
        {cardContent}
      </Link>
    ) : (
      cardContent
    );
  })}
</div>








{/* AR Mobile-only Prompt Modal */}
<AnimatePresence>
{arPromptFor && (
  <motion.div
    className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    aria-modal="true" role="dialog"
  >
    <motion.div
      className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
    >
      <h3 className="text-xl font-semibold text-black mb-2">
        {translatedText.text16}
      </h3>
      <p className="text-sm text-black mb-4">
        {translatedText.text17}
      </p>

      <div className="w-full flex justify-center mb-4">
        <img
          src="https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/media/Teche/QR_to_TECHE.jpg"
          alt="QR code per app Te.C.H-e"
          className="w-full max-w-[260px] h-auto rounded-lg border"
          loading="lazy"
        />
      </div>

      <div className="flex gap-3 justify-end">
        <button
          onClick={cancelEnableAR}
          className="px-4 py-2 rounded-lg border border-gray-300 text-black"
          disabled={arPromptLoading}
        >
          {translatedText.text19}
        </button>
        <button
          onClick={confirmEnableAR}
          className="px-4 py-2 rounded-lg bg-[#324B8E] text-white disabled:opacity-60"
          disabled={arPromptLoading}
        >
          {translatedText.text18}
        </button>
      </div>
    </motion.div>
  </motion.div>
)}
</AnimatePresence>





</div>






  );
};

export default Carrello;
